'use client'

import React, {createContext, useCallback, useEffect, useMemo, useState} from 'react'

type UiPreferences = {
    compact: boolean
    reduceMotion: boolean
    weekStartsMonday: boolean
    notifications: boolean
    experimental: boolean
}

type PreferencesContextValue = {
    prefs: UiPreferences
    setPrefs: React.Dispatch<React.SetStateAction<UiPreferences>>
    updatePref: <K extends keyof UiPreferences>(key: K, value: UiPreferences[K]) => void
}

const defaultPrefs: UiPreferences = {
    compact: false,
    reduceMotion: false,
    weekStartsMonday: true,
    notifications: true,
    experimental: false,
}

const STORAGE_KEY = 'fitro-ui-prefs'
const Ctx = createContext<PreferencesContextValue | undefined>(undefined)

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [prefs, setPrefs] = useState<UiPreferences>(() => {
        if (typeof window === 'undefined') return defaultPrefs
        try {
            const raw = localStorage.getItem(STORAGE_KEY)
            return raw ? {...defaultPrefs, ...(JSON.parse(raw) as UiPreferences)} : defaultPrefs
        } catch {
            return defaultPrefs
        }
    })

    useEffect(() => {
        if (typeof window === 'undefined') return
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
        const root = document.documentElement
        root.classList.toggle('compact', !!prefs.compact)
        root.dataset.reduceMotion = prefs.reduceMotion ? 'true' : 'false'
        root.dataset.weekStartsMonday = prefs.weekStartsMonday ? 'true' : 'false'
        root.dataset.experimental = prefs.experimental ? 'true' : 'false'
    }, [prefs])

    const updatePref = useCallback(<K extends keyof UiPreferences>(key: K, value: UiPreferences[K]) => {
        setPrefs(prev => ({...prev, [key]: value}))
    }, [])

    const value = useMemo<PreferencesContextValue>(() => ({prefs, setPrefs, updatePref}), [prefs, updatePref])

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
